import { supabase } from "@/lib/supabase";
import type { Order } from "@/types";
import { useCallback, useEffect, useState } from "react";

interface UseOrdersOptions {
	storeId?: string;
	status?: string;
	page?: number;
	pageSize?: number;
}

export function useOrders(options: UseOrdersOptions = {}) {
	const { storeId, status, page = 1, pageSize = 25 } = options;
	const [orders, setOrders] = useState<Order[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchOrders = useCallback(async () => {
		setLoading(true);
		setError(null);

		let query = supabase
			.from("orders")
			.select("*, store:stores(name, shopify_domain)", { count: "exact" })
			.order("created_at", { ascending: false })
			.range((page - 1) * pageSize, page * pageSize - 1);

		if (storeId) {
			query = query.eq("store_id", storeId);
		}
		if (status) {
			query = query.eq("status", status);
		}

		const { data, error: err, count } = await query;

		if (err) {
			setError(err.message);
		} else {
			setOrders(data ?? []);
			setTotal(count ?? 0);
		}
		setLoading(false);
	}, [storeId, status, page, pageSize]);

	useEffect(() => {
		fetchOrders();
	}, [fetchOrders]);

	return { orders, total, loading, error, refetch: fetchOrders };
}

export function useOrder(id: string | undefined) {
	const [order, setOrder] = useState<Order | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!id) {
			setLoading(false);
			return;
		}

		async function fetch() {
			setLoading(true);
			const { data, error: err } = await supabase
				.from("orders")
				.select("*, store:stores(name, shopify_domain), message:messages(*)")
				.eq("id", id)
				.single();

			if (err) {
				setError(err.message);
			} else {
				setOrder(data);
			}
			setLoading(false);
		}

		fetch();
	}, [id]);

	return { order, loading, error };
}
